/**
 * RTK Query endpoints for chat item pinning via the agents API.
 * Every ChatItem (messages, tool-calls, content-briefs, ...) can be pinned.
 */

import type {
  BaseQueryFn,
  EndpointBuilder,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query'

import type {
  ListPinsArgs,
  PinItemArgs,
  PinListResponseDto,
  PinResponseDto,
  UnpinItemArgs,
} from '../interfaces/message-pin.interface'
import { agentsApiSlice } from './agents-api.store'

const CONVERSATIONS_API = '/api/v1/conversations'
const OPTIMISTIC_PIN_ID_PREFIX = 'optimistic-'

type PinBuilder = EndpointBuilder<
  BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
  | 'ChatModels'
  | 'MultimodalModels'
  | 'ImageModels'
  | 'AgentTools'
  | 'Agencies'
  | 'Conversations'
  | 'Conversation'
  | 'Messages'
  | 'PromptTemplates'
  | 'Connectors'
  | 'ConnectorProjects',
  'agentsApi'
>

const buildOptimisticPin = (args: PinItemArgs): PinResponseDto => ({
  id: `${OPTIMISTIC_PIN_ID_PREFIX}${args.itemId}`,
  conversation_id: args.conversationId,
  item_id: args.itemId,
  parent_message_id: args.body.parent_message_id ?? null,
  pinned_by: { user_id: 'self', display_name: null, avatar_url: null },
  pinned_at: new Date().toISOString(),
  item_preview: args.body.item_preview,
  item_role: args.body.item_role,
  parent_created_at: null,
  page_number: Math.max(1, args.optimisticPageNumber),
  page_size: args.pageSize,
})

const toListArgs = (
  args: Pick<PinItemArgs, 'conversationId' | 'pageSize' | 'includeNotes'>
): ListPinsArgs => ({
  conversationId: args.conversationId,
  pageSize: args.pageSize,
  includeNotes: args.includeNotes,
})

const buildListPinsEndpoint = (builder: PinBuilder) =>
  builder.query<PinListResponseDto, ListPinsArgs>({
    query: ({ conversationId, pageSize, includeNotes }) => ({
      url: `${CONVERSATIONS_API}/${conversationId}/pins`,
      params: { page_size: pageSize, include_notes: includeNotes },
    }),
  })

const applyOptimisticPinInsert = (args: PinItemArgs, draft: PinListResponseDto): void => {
  draft.items.unshift(buildOptimisticPin(args))
  draft.total += 1
}

const replaceOptimisticPinWithServerEntry = (
  args: PinItemArgs,
  data: PinResponseDto,
  draft: PinListResponseDto
): void => {
  const optimisticIndex = draft.items.findIndex(
    (pin) => pin.id === `${OPTIMISTIC_PIN_ID_PREFIX}${args.itemId}`
  )
  if (optimisticIndex >= 0) draft.items[optimisticIndex] = data
}

const removePinByItemId = (itemId: string, draft: PinListResponseDto): void => {
  const before = draft.items.length
  draft.items = draft.items.filter((pin) => pin.item_id !== itemId)
  draft.total -= before - draft.items.length
}

const buildPinItemEndpoint = (builder: PinBuilder) =>
  builder.mutation<PinResponseDto, PinItemArgs>({
    query: ({ conversationId, itemId, body, pageSize, includeNotes }) => ({
      url: `${CONVERSATIONS_API}/${conversationId}/items/${encodeURIComponent(itemId)}/pin`,
      method: 'POST',
      params: { page_size: pageSize, include_notes: includeNotes },
      body,
    }),
    async onQueryStarted(args, { dispatch, queryFulfilled }) {
      const patch = dispatch(
        messagePinEndpoints.util.updateQueryData('listPins', toListArgs(args), (draft) => {
          applyOptimisticPinInsert(args, draft)
        })
      )
      try {
        const { data } = await queryFulfilled
        dispatch(
          messagePinEndpoints.util.updateQueryData('listPins', toListArgs(args), (draft) => {
            replaceOptimisticPinWithServerEntry(args, data, draft)
          })
        )
      } catch {
        patch.undo()
      }
    },
    // No invalidatesTags: the optimistic patch plus the WebSocket
    // `message.pinned` broadcast already converge the cache for self
    // and other participants. Invalidating here would trigger a third
    // redundant refetch and visible row flicker.
  })

const buildUnpinItemEndpoint = (builder: PinBuilder) =>
  builder.mutation<void, UnpinItemArgs>({
    query: ({ conversationId, itemId }) => ({
      url: `${CONVERSATIONS_API}/${conversationId}/items/${encodeURIComponent(itemId)}/pin`,
      method: 'DELETE',
    }),
    async onQueryStarted(args, { dispatch, queryFulfilled }) {
      const patch = dispatch(
        messagePinEndpoints.util.updateQueryData('listPins', toListArgs(args), (draft) => {
          removePinByItemId(args.itemId, draft)
        })
      )
      try {
        await queryFulfilled
      } catch {
        patch.undo()
      }
    },
    // No invalidatesTags: same reasoning as pinItem — optimistic removal
    // plus WS `message.unpinned` is sufficient for both self and peers.
  })

export const messagePinEndpoints = agentsApiSlice.injectEndpoints({
  endpoints: (builder: PinBuilder) => ({
    listPins: buildListPinsEndpoint(builder),
    pinItem: buildPinItemEndpoint(builder),
    unpinItem: buildUnpinItemEndpoint(builder),
  }),
})

export const { useListPinsQuery, usePinItemMutation, useUnpinItemMutation } = messagePinEndpoints
